import * as configcat from 'configcat-js';

const configCatClient = configcat.getClient(
  process.env.VUE_APP_CONFIG_CAT_KEY,
  configcat.PollingMode.ManualPoll,
);
const REFRESH_CONFIG_TIMEOUT_MS = 1000 * 60; // 60 seconds;
const LAST_REFRESH_EPOCH_STORAGE_KEY = 'LastConfigDownloadEpoch';
const DEBUG_MODE = false;
let refreshInProgress = false;

/**
 * @typedef {'PHQ'|'REPORT'|'ALLOW_BETA_SIGNUP'} FeatureName
 * @typedef {'MEDICATION_OPT_OUT'} GloabalFlagName
 */

/**
 * Checks if feature is enabled
 * Assumes user is logged in
 * @param {import("vuex").Store} store
 * @param {GloabalFlagName} featureName
 * @returns {Promise<boolean>}
 */
export async function isGlobalFlagEnabled(store, featureName) {
  return getBoolValueAsync(store, featureName, false);
}

/**
 * Checks if feature is enabled
 * Assumes user is logged in
 * @param {import("vuex").Store} store
 * @param {FeatureName} featureName
 * @returns {Promise<boolean>}
 */
export async function isFeatureEnabled(store, featureName) {
  return getBoolValueAsync(store, `VUE_APP_FEATURE_${featureName}`, false);
}

/**
 * Checks if feature is enabled
 * Assumes user is logged in
 * @param {import("vuex").Store} store
 * @param {string} key
 * @returns {Promise<string|null>}
 */
export async function getStringConfig(store, key) {
  // @ts-ignore
  return getValueAsync(store, key, null);
}

/**
 * Get bool value from config cat
 * Assumes user is logged in
 * @param {import("vuex").Store} store
 * @param {string} key key to get value for
 * @param {boolean} defaultValue default value
 * @returns {Promise<boolean>}
 */
async function getBoolValueAsync(store, key, defaultValue) {
  const value = await getValueAsync(store, key, defaultValue);
  return value === true;
}

/**
 * Get any value from config cat
 * Assumes user is logged in
 * @param {import("vuex").Store} store
 * @param {string} key key to get value for
 * @param {configcat.SettingValue} defaultValue default value
 * @returns {Promise<configcat.SettingValue>}
 */
async function getValueAsync(store, key, defaultValue) {
  if (shouldRefreshCache() && !refreshInProgress) {
    refreshInProgress = true;
    updateLastConfigDownloadEpoch();
    console.log('Refreshing config cat cache');
    const startTime = new Date().getTime();
    await configCatClient.forceRefreshAsync();
    if (DEBUG_MODE) {
      console.log(`Force refresh done in ${new Date().getTime() - startTime}ms`);
    }
    refreshInProgress = false;
  }
  if (refreshInProgress) {
    if (DEBUG_MODE) {
      console.log('Waiting for refresh to complete');
    }
    while (refreshInProgress) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }

  const concatUser = getConfigCatUser(store);
  const value = await configCatClient.getValueAsync(key, defaultValue, concatUser);
  if (DEBUG_MODE) {
    console.log(`Got value for ${key}: ${value}`);
  }
  return value;
}

/**
 * Get the configcat user object or undefined if user is not logged in
 * @param {import("vuex").Store} store
 * @returns {configcat.User|undefined}
 */
function getConfigCatUser(store) {
  const { me } = store.state.api;
  if (!me) {
    return undefined;
  }
  const licensesArray = me.licenses;
  const customParameters = { userType: me.type };
  if (licensesArray.length > 0) {
    customParameters.licenseGroup = licensesArray[licensesArray.length - 1].groupId;
  }
  return new configcat.User(me.id, '', '', customParameters);
}

/**
 * Update last config download epoch to now
 */
function updateLastConfigDownloadEpoch() {
  localStorage.setItem(LAST_REFRESH_EPOCH_STORAGE_KEY, new Date().getTime().toString());
}

/**
 * Returns true if cache should be refreshed
 * @returns {boolean}
 */
function shouldRefreshCache() {
  const lastConfigDownloadEpoch = localStorage.getItem(LAST_REFRESH_EPOCH_STORAGE_KEY);
  if (lastConfigDownloadEpoch === null || lastConfigDownloadEpoch === undefined) {
    return true;
  }
  const epochNow = new Date().getTime();
  const timeDiff = epochNow - +lastConfigDownloadEpoch;
  return timeDiff > REFRESH_CONFIG_TIMEOUT_MS;
}
